import { queryOptions } from '@tanstack/react-query';

import { api } from '@/lib/api/api';
import type {
  NotificationPreferences,
  NotificationPreferenceHistory,
} from '@/lib/api/domains/notification-preferences';
import type { UserId } from '@/lib/api/ids';
import { keys } from '@/lib/query-keys';

/*
 * The client user's notification-preference read (F02 R35). How the portal
 * CONSUMES the `GET /users/:id/notification-preferences` transport: the
 * queryOptions the preference screen reads.
 *
 * Keyed by the account id the preferences are held FOR (per person,
 * AC-3d6561d1). The server has already applied DEFAULT-ON for any never-answered
 * key (AC-f00ecffd), so the billing notification reads `true` for a new client
 * user (AC-e0a73de2) unless turned off (AC-a538e368) — the screen renders what
 * the server returns and never computes the default itself. The id is opaque,
 * never PII (AGENTS.md — ids only).
 */
export function notificationPreferencesQuery(userId: UserId) {
  return queryOptions<NotificationPreferences>({
    queryKey: keys.notificationPreferences.forUser(userId),
    queryFn: () => api.notificationPreferences.get(userId),
  });
}

/*
 * The notification-preference CHANGE HISTORY read (F02 R28/R29). How the portal
 * (and the account history surface) CONSUMES the
 * `GET /users/:id/notification-preferences/history` transport: each changed
 * choice's value BEFORE and AFTER (AC-b1d8996b) and WHO changed it — the
 * user's own on a self-change (AC-58e727f7), the named person on an on-behalf
 * change (AC-bf8668ea). The server resolves the changer's NAME (naming the
 * person, never the organization) and the frontend renders it; the id is opaque,
 * never PII (AGENTS.md — ids only). Keyed under the user's preferences so a
 * write invalidates both the current choices and the history.
 */
export function notificationPreferenceHistoryQuery(userId: UserId) {
  return queryOptions<NotificationPreferenceHistory>({
    queryKey: keys.notificationPreferences.history(userId),
    queryFn: () => api.notificationPreferences.getHistory(userId),
  });
}
